import { 
  DiaNaoFuncionamento, 
  CreateDiaNaoFuncionamentoRequest, 
  UpdateDiaNaoFuncionamentoRequest,
  DiaNaoFuncionamentoListResponse,
  DiaNaoFuncionamentoStatistics,
  DiaNaoFuncionamentoEstatisticasDetalhadas,
  DiaNaoFuncionamentoFilters,
  VerificacaoDataResponse,
  ApiResponse
} from '@/types/diaNaoFuncionamento';

import { fetcher } from '@/lib/api';
import axios from 'axios';

// NOVOS TIPOS para sincronização de feriados
export interface SincronizacaoFeriadosResponse {
  success: boolean;
  message: string;
  dados: {
    importados: number;
    atualizados: number;
    erros: string[];
  };
}

export interface ImportacaoFeriadosResponse {
  success: boolean;
  message: string;
  dados: {
    ano: number;
    quantidade: number;
    feriados: FeriadoImportado[];
  };
}

export interface FeriadoImportado {
  data: string;
  nome: string;
  tipo: string;
  descricao: string;
  automatico: boolean;
}

export interface AgendamentoResponse {
  success: boolean;
  message: string;
  agendamento: string;
}

class DiaNaoFuncionamentoServiceClass {
  // Usamos URLs relativas para as chamadas HTTP

  /**
   * NOVA FUNCIONALIDADE: Sincronizar feriados nacionais automaticamente
   */
  async sincronizarFeriados(ano?: number): Promise<SincronizacaoFeriadosResponse> {
    const url = ano ? `/api/dias-nao-funcionamento/sincronizar-feriados?ano=${ano}` : '/api/dias-nao-funcionamento/sincronizar-feriados';
    return await fetcher<SincronizacaoFeriadosResponse>({ method: 'post', url });
  }

  /**
   * NOVA FUNCIONALIDADE: Importar feriados de um ano específico (preview)
   */
  async importarFeriadosAno(ano: number): Promise<ImportacaoFeriadosResponse> {
    if (ano < 2020 || ano > 2030) {
      throw new Error('Ano deve estar entre 2020 e 2030');
    }
    const url = `/api/dias-nao-funcionamento/importar-feriados/${ano}`;
    const result = await fetcher<ImportacaoFeriadosResponse>({ method: 'get', url });
    if (!result.success) {
      throw new Error(result.message || 'Resposta inválida da API');
    }
    return result;
  }

  /**
   * NOVA FUNCIONALIDADE: Agendar sincronização automática
   */
  async agendarSincronizacao(): Promise<AgendamentoResponse> {
    return await fetcher<AgendamentoResponse>({ method: 'post', url: '/api/dias-nao-funcionamento/agendar-sincronizacao' });
  }

  // Buscar todos os dias não funcionamento com filtros (ATUALIZADO)
  async getDiasNaoFuncionamento(filters?: DiaNaoFuncionamentoFilters & { automatico?: boolean }): Promise<DiaNaoFuncionamentoListResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.ativo !== undefined) params.append('ativo', filters.ativo.toString());
      if (filters.automatico !== undefined) params.append('automatico', filters.automatico.toString()); // NOVO
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
    } else {
      // ✅ CORREÇÃO: Buscar TODOS os feriados sem paginação
      params.append('limit', '1000'); // Limite alto para pegar todos
    }

    const url = params.toString() ? `/api/dias-nao-funcionamento?${params}` : '/api/dias-nao-funcionamento?limit=1000';
    return await fetcher<DiaNaoFuncionamentoListResponse>({ method: 'get', url });
  }

  // Buscar dia não funcionamento por ID
  async getDiaNaoFuncionamentoById(id: string): Promise<DiaNaoFuncionamento> {
    try {
      const response = await axios.get(`/api/dias-nao-funcionamento/${id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true
      });

      if (!response.data || !response.data.success) {
        const errorData = response.data?.error || response.data?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorData);
      }

      return response.data.diaNaoFuncionamento;
    } catch (error) {
      
      throw new Error(error instanceof Error ? error.message : 'Erro ao buscar dia não funcionamento');
    }
  }

  // Criar novo dia não funcionamento
  async createDiaNaoFuncionamento(data: CreateDiaNaoFuncionamentoRequest): Promise<DiaNaoFuncionamento> {
    return await fetcher<DiaNaoFuncionamento>({ method: 'post', url: '/api/dias-nao-funcionamento', data });
  }

  // Atualizar dia não funcionamento
  async updateDiaNaoFuncionamento(id: string, data: UpdateDiaNaoFuncionamentoRequest): Promise<DiaNaoFuncionamento> {
    return await fetcher<DiaNaoFuncionamento>({ method: 'put', url: `/api/dias-nao-funcionamento/${id}`, data });
  }

  // Excluir dia não funcionamento
  async deleteDiaNaoFuncionamento(id: string): Promise<void> {
    await fetcher<void>({ method: 'delete', url: `/api/dias-nao-funcionamento/${id}` });
  }

  // Alternar status do dia não funcionamento
  async toggleStatus(id: string): Promise<DiaNaoFuncionamento> {
    return await fetcher<DiaNaoFuncionamento>({ method: 'patch', url: `/api/dias-nao-funcionamento/${id}/status` });
  }

  // Verificar se uma data é dia não funcionamento
  async verificarData(data: string): Promise<VerificacaoDataResponse> {
    try {
      const response = await axios.get(`/api/dias-nao-funcionamento/verificar/${data}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true
      });

      if (!response.data || !response.data.success) {
        const errorData = response.data?.error || response.data?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorData);
      }

      return response.data.verificacaoData;
    } catch (error) {
      
      throw new Error(error instanceof Error ? error.message : 'Erro ao verificar data');
    }
  }

  // Obter estatísticas (ATUALIZADO para usar a nova versão com automáticos)
  async getEstatisticas(): Promise<DiaNaoFuncionamentoEstatisticasDetalhadas> {
    const result = await fetcher<{ estatisticas: DiaNaoFuncionamentoEstatisticasDetalhadas }>({ method: 'get', url: '/api/dias-nao-funcionamento/estatisticas' });
    return result.estatisticas;
  }

  // Alias para manter compatibilidade
  async getStatistics(): Promise<DiaNaoFuncionamentoStatistics> {
    const estatisticas = await this.getEstatisticas();
    
    // Converter para o formato antigo se necessário
    return {
      totalDias: estatisticas.geral.total,
      diasAtivos: estatisticas.geral.ativos,
      diasInativos: estatisticas.geral.inativos,
      diasRecorrentes: estatisticas.geral.recorrentes,
      porTipo: estatisticas.porTipo
    };
  }

  // Obter estatísticas detalhadas (método adicional)
  async getEstatisticasDetalhadas(): Promise<DiaNaoFuncionamentoEstatisticasDetalhadas> {
    return this.getEstatisticas();
  }

  // NOVOS MÉTODOS UTILITÁRIOS

  /**
   * Verifica se um dia não funcionamento é automático (feriado nacional)
   */
  isAutomatico(dia: DiaNaoFuncionamento): boolean {
    return dia.automatico === true;
  }

  /**
   * Filtra apenas feriados automáticos
   */
  getFeriadosAutomaticos(dias: DiaNaoFuncionamento[]): DiaNaoFuncionamento[] {
    return dias.filter(dia => this.isAutomatico(dia));
  }

  /**
   * Filtra apenas dias não funcionamento manuais
   */
  getDiasManuais(dias: DiaNaoFuncionamento[]): DiaNaoFuncionamento[] {
    return dias.filter(dia => !this.isAutomatico(dia));
  }

  /**
   * Obtém próximos feriados nacionais
   */
  getProximosFeriadosNacionais(dias: DiaNaoFuncionamento[], limite: number = 5): DiaNaoFuncionamento[] {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    return this.getFeriadosAutomaticos(dias)
      .filter(dia => new Date(dia.data) >= hoje)
      .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
      .slice(0, limite);
  }

  /**
   * Formata o tipo de origem do dia não funcionamento
   */
  formatarOrigem(dia: DiaNaoFuncionamento): string {
    return this.isAutomatico(dia) ? 'Automático' : 'Manual';
  }

  /**
   * Obtém a cor do badge baseado na origem
   */
  getCorOrigem(dia: DiaNaoFuncionamento): string {
    return this.isAutomatico(dia) ? 'blue' : 'orange';
  }

  // Métodos utilitários para formatação
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatDateToInput(dateString: string): string {
    return new Date(dateString).toISOString().split('T')[0];
  }

  isDataPassada(dateString: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dataComparacao = new Date(dateString);
    dataComparacao.setHours(0, 0, 0, 0);
    return dataComparacao < today;
  }

  isDataFutura(dateString: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dataComparacao = new Date(dateString);
    dataComparacao.setHours(0, 0, 0, 0);
    return dataComparacao > today;
  }

  isDomingo(dateString: string): boolean {
    return new Date(dateString).getDay() === 0;
  }

  // Validar formulário
  validateFormData(data: CreateDiaNaoFuncionamentoRequest | UpdateDiaNaoFuncionamentoRequest): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if ('data' in data && data.data) {
      const dataObj = new Date(data.data);
      if (isNaN(dataObj.getTime())) {
        errors.push('Data inválida');
      }
    }

    if ('motivo' in data && data.motivo !== undefined) {
      if (!data.motivo || data.motivo.trim().length === 0) {
        errors.push('Motivo é obrigatório');
      } else if (data.motivo.length > 200) {
        errors.push('Motivo não pode ter mais de 200 caracteres');
      }
    }

    if ('descricao' in data && data.descricao && data.descricao.length > 500) {
      errors.push('Descrição não pode ter mais de 500 caracteres');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Agrupar dias por mês
  groupByMonth(dias: DiaNaoFuncionamento[]): Record<string, DiaNaoFuncionamento[]> {
    return dias.reduce((acc, dia) => {
      const date = new Date(dia.data);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      
      acc[monthKey].push(dia);
      return acc;
    }, {} as Record<string, DiaNaoFuncionamento[]>);
  }

  // Obter próximos dias não funcionamento
  getProximosDias(dias: DiaNaoFuncionamento[], limite: number = 5): DiaNaoFuncionamento[] {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    return dias
      .filter(dia => dia.ativo && new Date(dia.data) >= hoje)
      .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
      .slice(0, limite);
  }
}

export const DiaNaoFuncionamentoService = new DiaNaoFuncionamentoServiceClass(); 